import { pathEq } from 'ramda';
import { useEffect } from 'react';

import { CLICK_MARGIN, NAV_HEIGHT } from '../measurements';
import { type RegisteredTarget } from '../navigation-context';

export const useScrollingIndexUpdate = (
  targets: RegisteredTarget[],
  onIndexUpdate: (targetTitle: string, goNextIndex: boolean) => void
): void => {
  // Effect for keeping track of the selected state as user scrolls
  useEffect(() => {
    // 2px gap between the threshold for normal scrolling and where the tab
    // buttons leave users when clicking tabs to scroll
    const rootMargin = NAV_HEIGHT + CLICK_MARGIN + 2;

    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          // We only want elements with negative top (hidden above the viewport)
          if (entry.boundingClientRect.top >= 0) return;

          const target = targets.find(pathEq(['ref', 'current'], entry.target));

          // Do nothing if this element has no navigation tab ref
          if (!target) return;

          // NOT intersecting means it's leaving the viewport (scrolling down).
          // Therefore the NEXT element's tab should be highlighted if exists.

          // Otherwise intersecting means entering the viewport (scrolling up).
          // So this target's tab should be highlighted.
          const goNextIndex = !entry.isIntersecting;

          onIndexUpdate(target.title, goNextIndex);
        }),
      { rootMargin: `-${rootMargin}px 0px 0px` }
    );

    targets.forEach(({ ref }: RegisteredTarget): void => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, [onIndexUpdate, targets]);
};
